import React, { useState } from 'react';
import { FaTrash, FaPen, FaBriefcase } from 'react-icons/fa';
import { SectionCard, Field, TextInput, TextArea, SaveRow, AddButton, EmptyHint, palette } from './ui';

const EMPTY_FORM = { title: '', company: '', location: '', start_date: '', end_date: '', is_current: false, description: '' };

function ExperienceForm({ isDarkMode, initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const ok = await onSave({ ...form, end_date: form.is_current ? null : form.end_date || null });
    setSaving(false);
    if (ok) onCancel();
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
      <Field label="Job title *">
        <TextInput isDarkMode={isDarkMode} value={form.title} onChange={set('title')} placeholder="e.g. Driver, Sales Executive" />
      </Field>
      <Field label="Company / Employer">
        <TextInput isDarkMode={isDarkMode} value={form.company} onChange={set('company')} placeholder="Company name" />
      </Field>
      <Field label="Location">
        <TextInput isDarkMode={isDarkMode} value={form.location} onChange={set('location')} placeholder="City" />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From">
          <TextInput isDarkMode={isDarkMode} type="month" value={form.start_date} onChange={set('start_date')} />
        </Field>
        <Field label="To">
          <TextInput isDarkMode={isDarkMode} type="month" value={form.end_date} onChange={set('end_date')} disabled={form.is_current} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <input
          type="checkbox"
          checked={form.is_current}
          onChange={e => setForm(f => ({ ...f, is_current: e.target.checked }))}
          className="h-4 w-4"
        />
        I currently work here
      </label>
      <Field label="What did you do there?" className="sm:col-span-2">
        <TextArea isDarkMode={isDarkMode} value={form.description} onChange={set('description')} placeholder="Short description of your work" />
      </Field>
      <div className="sm:col-span-2">
        <SaveRow isDarkMode={isDarkMode} onSave={save} onCancel={onCancel} saving={saving} />
      </div>
    </div>
  );
}

const formatRange = (item) => {
  const fmt = (v) => {
    if (!v) return '';
    const d = new Date(`${v.length === 7 ? `${v}-01` : v}`);
    return isNaN(d) ? v : d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
  };
  const from = fmt(item.start_date);
  const to = item.is_current ? 'Present' : fmt(item.end_date);
  return [from, to].filter(Boolean).join(' – ');
};

export default function ExperienceCard({ isDarkMode, items, actions }) {
  const c = palette(isDarkMode);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);

  return (
    <SectionCard id="experience" title="Work experience" isDarkMode={isDarkMode}>
      {items.length === 0 && !adding && (
        <EmptyHint isDarkMode={isDarkMode}>Add your past or current work — even informal work counts.</EmptyHint>
      )}
      <ul className="space-y-4">
        {items.map(item => (
          <li key={item.id} className="flex gap-3">
            <FaBriefcase className={`h-4 w-4 mt-1 shrink-0 ${c.secondaryText}`} />
            <div className="flex-1 min-w-0">
              {editingId === item.id ? (
                <ExperienceForm
                  isDarkMode={isDarkMode}
                  initial={{ ...EMPTY_FORM, ...item }}
                  onSave={(data) => actions.update(item.id, data)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <>
                  <p className={`font-medium text-sm ${c.text}`}>{item.title}</p>
                  <p className={`text-sm ${c.secondaryText}`}>
                    {[item.company, item.location].filter(Boolean).join(' · ')}
                  </p>
                  <p className={`text-xs ${c.secondaryText}`}>{formatRange(item)}</p>
                  {item.description && <p className={`text-sm mt-1 ${c.text}`}>{item.description}</p>}
                </>
              )}
            </div>
            {editingId !== item.id && (
              <div className="flex gap-1 shrink-0">
                <button onClick={() => { setEditingId(item.id); setAdding(false); }} aria-label="Edit" className={`p-2 rounded-full ${c.hover} ${c.secondaryText}`}>
                  <FaPen className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => actions.remove(item.id)} aria-label="Delete" className={`p-2 rounded-full ${c.hover} text-red-500`}>
                  <FaTrash className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {adding ? (
        <ExperienceForm
          isDarkMode={isDarkMode}
          initial={EMPTY_FORM}
          onSave={(data) => actions.add(data)}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <div className="mt-3">
          <AddButton isDarkMode={isDarkMode} label="Add experience" onClick={() => { setAdding(true); setEditingId(null); }} />
        </div>
      )}
    </SectionCard>
  );
}
